<template>
  <div class="order-table-container small">
    <b-modal
      v-if="shouldShowRejectionModal && selectedItemToReject"
      title="Please add the reason to reject"
      v-model="shouldShowRejectionModal"
      size="sm"
      centered no-close-on-backdrop
      @cancel="shouldShowRejectionModal = false"
      @close="shouldShowRejectionModal = false"
      @ok="rejectPermission">
      <div>
        <p>
          <b>Customer Number:</b> {{ selectedItemToReject.CustomerNumber }}
        </p>
        <p>
          <b>Customer Name:</b> {{ selectedItemToReject.CustomerName }}
        </p>
        <p>
          <b>User Email:</b> {{ selectedItemToReject.UserEmail }}
        </p>
        <b-form-group
          label="Rejection reason*"
          label-for="rejection-reason">
          <b-form-input id="rejection-reason" v-model="rejectionReason" placeholder="Rejection reason" :state="isValidRejectionReason()"></b-form-input>
          <b-form-invalid-feedback>Please add a rejection reason</b-form-invalid-feedback>
        </b-form-group>
      </div>
    </b-modal>
    <b-modal
      v-model="showApproveModal"
      v-if="selectedItemToApprove != null"
      title="Are you sure you want to approve?"
      size="sm"
      centered
      no-close-on-backdrop
      @cancel="showApproveModal = false"
      @close="showApproveModal = false"
      @ok="approvePermission">
      <div>
        <p>
          <b>Customer Number:</b> {{ selectedItemToApprove.CustomerNumber }}
        </p>
        <p>
          <b>Customer Name:</b> {{ selectedItemToApprove.CustomerName }}
        </p>
        <p>
          <b>User Email:</b> {{ selectedItemToApprove.UserEmail }}
        </p>
      </div>
    </b-modal>
    <div class="my-1">
      <b-navbar type="light" variant="light" class="actions-bar p-0">
        <b-navbar-nav class="hide-for-mobile" v-if="isFiltered || isPrefiltered">
          <b-nav-item>
            <b-button
              variant="outline-primary"
              class="primary-outline-btn"
              @click="clearFilters"
              size="sm"
              data-cy="reset-filters"
            >
              <b-icon icon="x-circle"></b-icon>
              <b class="ml-2">Reset Filter</b>
            </b-button>
          </b-nav-item>
        </b-navbar-nav>
        <b-navbar-nav class="reset-filter-icon">
          <b-nav-item>
            <b-button
              variant="flat"
              class="primary-outline-btn p-1 color-primary"
              @click="filterModalVisible = true">
              <b-icon
                :icon="(isFiltered || isPrefiltered) ? 'filter-circle-fill' : 'filter-circle'"
                @click="filterModalVisible = true"></b-icon> Filter
            </b-button>
          </b-nav-item>
        </b-navbar-nav>
      </b-navbar>
    </div>
    <div
      class="filter-pills"
      v-if="isFiltered">
      Filtered By:
      <b-badge
        v-for="(value, key) in activeFilters"
        variant="warning"
        pill
        :key="'filter-badge' + key"
        href="#"
        @click="filter[key] = ''">{{ value }} <b-icon icon="x-circle-fill" class="ml-1"></b-icon></b-badge>
    </div>
    <b-table
      :items="customers.List"
      :fields="fields"
      responsive
      hover
      borderless
      class="order-table"
      :busy="customers.loading"
      thead-class="th-custom-class">
      <template #table-busy>
        <div class="text-center my-2">
          <b-spinner class="align-middle primary-color mr-2"></b-spinner>
          <strong class="primary-color">Loading...</strong>
        </div>
      </template>
      <template v-slot:head(details)>
        <div></div>
      </template>
      <template v-slot:cell(details)="row">
        <b-icon
          :icon="row.detailsShowing ? 'chevron-up' : 'chevron-down'"
          @click="row.toggleDetails">
        </b-icon>
      </template>
      <template v-slot:cell(CustomerNumber)="{ item }">
        <router-link
          :to="{
            name: 'customer',
            params: { customerNumber: item.CustomerNumber },
          }">
          {{ item.CustomerNumber }}
        </router-link>
      </template>
      <template v-for="field in fields.filter((f) => f.isSortable || f.isFilterable)" v-slot:[`head(${field.key})`]="{}">
        <div :key="field.key">
          <div class="d-flex align-items-center">
            <div class="d-flex align-items-center">
              <template>
                {{ field.label }}
              </template>
              <b-dropdown dropright no-caret size="sm" variant="link" boundary="window" block>
                <template #button-content>
                  <b-icon
                    v-if="field.isFilterable"
                    :icon="filter[field.key].length > 0 || filter[field.key] === true
                      ? 'filter-circle-fill'
                      : 'filter-circle'
                      "
                    :variant="filter[field.key] !== '' ? 'primary' : 'secondary'"
                    class="header-icon order-filters"
                    :id="'filter-' + field.key"></b-icon>
                </template>
                <div class="px-2">
                  <label class="d-flex align-items-center small">
                    <template>
                      {{ field.label }}
                    </template>
                    <template v-if="field.isSortable">
                      <b-icon
                        v-if="sortOptions.by === field.key"
                        @click="sort(field.key)"
                        :icon="sortOptions.desc ? 'sort-down' : 'sort-up'"
                        class="header-icon ml-2 order-filters"></b-icon>
                      <b-icon
                        v-else
                        @click="sort(field.key)"
                        :icon="'sort-down'"
                        class="header-icon ml-2 order-filters"
                        style="color: #000 !important"></b-icon>
                    </template>
                  </label>
                  <b-input-group size="sm" class="mt-2">
                    <b-form-checkbox v-if="field.key === 'Users'" size="sm" placeholder="YYYY-MM-DD" autocomplete="off" debounce="1000" v-model="filter.Users">
                      Only sales reps with users
                    </b-form-checkbox>
                    <b-form-checkbox v-else-if="field.key === 'PermissionRequests'" size="sm" placeholder="YYYY-MM-DD" autocomplete="off" debounce="1000" v-model="filter.PermissionRequests">
                      Only with pending permission requests
                    </b-form-checkbox>
                    <b-form-input v-else :id="'filter' + field.key" type="text" :placeholder="generatePlaceHolder(field)" v-model.trim="filter[field.key]" debounce="1000"></b-form-input>
                    <b-input-group-append v-if="filter[field.key].length > 0"><b-input-group-text class="clear-btn" @click="filter[field.key] = ''"><b-icon icon="x"></b-icon></b-input-group-text></b-input-group-append>
                  </b-input-group>
                </div>
              </b-dropdown>
            </div>

            <template v-if="field.isSortable">
              <b-icon
                v-if="sortOptions.by === field.key"
                @click="sort(field.key)"
                :icon="sortOptions.desc ? 'sort-down' : 'sort-up'"
                class="header-icon ml-2 order-filters"></b-icon>
            </template>
          </div>
        </div>
      </template>
      <template v-slot:cell(City)="{ item }">
        {{ item.CustomerAddress.City }}
      </template>
      <template v-slot:cell(State)="{ item }">
        {{ item.CustomerAddress.Region }}
      </template>
      <template v-slot:cell(PostalCode)="{ item }">
        {{ item.CustomerAddress.PostalCode }}
      </template>
      <template v-slot:cell(PermissionRequests)="{ item }">
        <template v-if="item.PendingPermissionRequests && item.PendingPermissionRequests.length > 0">
          <div v-for="permission in item.PendingPermissionRequests" :key="permission.Id">
            {{ permission.UserEmail }}
            <b-button variant="link" @click="onApprove(permission)">
              Approve
            </b-button>
            <b-button variant="link" @click="onReject(permission)">
              Reject
            </b-button>
          </div>
        </template>
      </template>
      <template #row-details="row">
        <b-card>
          <b-row class="mb-2">
            <b-col cols="4" class="text-left">
              {{ row.item.Address.City }}
            </b-col>
            <b-col cols="4" class="text-left">
              {{ row.item.Address.Region }}
            </b-col>
            <b-col cols="4" class="text-left">
              {{ row.item.Address.PostalCode }}
            </b-col>
          </b-row>
          <b-button size="sm" class="mt-2 w-100" @click="row.toggleDetails" variant="outline"><b-icon icon="chevron-double-up" variant="link" /></b-button>
        </b-card>
      </template>
    </b-table>
    <div class="d-flex justify-content-center" md="my-5">
      <b-pagination v-if="customers.Page > 0" v-model="customers.Page" :total-rows="customers.Total" :per-page="customers.PageSize" limit="10" @change="(page) => pageChanged(page)">
      </b-pagination>
    </div>
    <b-modal v-model="filterModalVisible" title="Filter Data" centered size="xl" footer-class="justify-content-center">
      <b-container fluid>
        <b-row>
          <!-- Loop through filterable fields to generate columns -->
          <template v-for="field in fields.filter((f) => f.isFilterable)">
            <b-col :key="field.key" cols="12">
              <div class="mb-3">
                <label :for="field.key" class="small">{{ field.label }}</label>
                <b-icon
                  v-if="sortOptions.by === field.key"
                  @click="sort(field.key)"
                  :icon="sortOptions.desc ? 'sort-down' : 'sort-up'"
                  class="header-icon ml-2"></b-icon>
                <b-icon
                  v-else
                  @click="sort(field.key)"
                  :icon="'sort-down'"
                  class="header-icon ml-2"
                  style="color: lightgray !important"></b-icon>
                <b-input-group size="text-sm">
                  <b-form-checkbox
                    v-if="field.key === 'Users'"
                    size="sm"
                    placeholder="YYYY-MM-DD"
                    autocomplete="off"
                    debounce="1000"
                    v-model="filter.Users">
                    Only sales reps with users
                  </b-form-checkbox>
                  <b-form-checkbox
                    v-else-if="field.key === 'PermissionRequests'"
                    size="sm"
                    placeholder="YYYY-MM-DD"
                    autocomplete="off"
                    debounce="1000"
                    v-model="filter.PermissionRequests">
                    Only with pending permission requests
                  </b-form-checkbox>
                  <b-form-input
                    v-else
                    type="text"
                    :placeholder="'Filter ' + field.label"
                    v-model.trim="filter[field.key]"
                    debounce="1000"></b-form-input>
                  <b-input-group-append v-if="filter[field.key] && filter[field.key].length > 0"><b-input-group-text
                      class="clear-btn"
                      @click="filter[field.key] = ''"><b-icon icon="x"></b-icon></b-input-group-text></b-input-group-append>
                </b-input-group>
              </div>
            </b-col>
          </template>
        </b-row>
      </b-container>
      <div slot="modal-footer" class="filter-modal-footer">
        <div class="text-center my-2" v-if="customers.loading">
          <b-spinner class="align-middle primary-color mr-2"></b-spinner>
          <strong class="primary-color">Filtering...</strong>
        </div>
        <b-button variant="outline-primary" class="w-100 color-primary mb-2" @click="filterModalVisible = false" v-else size="sm">
          <b class="ml-2">See Results</b>
        </b-button>
        <b-button variant="outline-default" class="w-100" @click="clearFilters" size="sm" v-if="isFiltered && !customers.loading" data-cy="reset-filters">
          <b-icon icon="x-circle"></b-icon>
          <b class="ml-2">Reset Filter</b>
        </b-button>
      </div>
    </b-modal>
  </div>
</template>

<script lang="ts">
import { Component, Mixins, Watch } from 'vue-property-decorator'
import { namespace } from 'vuex-class'

// TODO: MxOrders?
import MxSite from '@/mixins/site'

import LoadingText from '@/components/utility/loading-text.vue'
import { ResponseArray } from '@/store/types'
import { isMobile } from '@/common/helpers/responsive-helper'
import { ICustomer, ICustomerFilterOn, ICustomerFilter, blankFilterOn, blankFilter, ICustomerSearchFilter } from '@/store/customer/types'
import _ from 'lodash'
import { Api } from '@/api/Api'
import { HttpMethod } from '@/api/FetchApi'
import { blankSearchFilters } from '@/store/customer/constants'

const nsCustomers = namespace('customer')

// import RequestListRequests from '@/components/request/request-list/requests.vue'
@Component({
  components: {
    'loading-text': LoadingText,
  },
})
export default class extends Mixins(MxSite) {
  private api = new Api()

  public currentPage = 1

  public filterOn: ICustomerFilterOn = _.assign({}, blankFilterOn)

  public filter: ICustomerFilter = _.assign({}, blankFilter)

  public searchTerm = ''

  public sortOptions: { by: string; desc: boolean } = {
    by: 'PermissionRequests',
    desc: true,
  }

  @nsCustomers.Action('getCustomers')
  private getCustomers: any

  @nsCustomers.State('customers')
  public customers!: ResponseArray<ICustomer>

  @nsCustomers.State('searchFilters')
  public searchFilters!: ICustomerSearchFilter

  public isMobile = isMobile()

  public filterModalVisible = false

  public rejectionReason: string | null = null

  public shouldShowRejectionModal = false
  public showApproveModal = false

  public selectedItemToReject: { Id: string, UserEmail: string, CustomerName: string, CustomerNumber: string } | null = null
  public selectedItemToApprove: { Id: string, UserEmail: string, CustomerName: string, CustomerNumber: string } | null = null

  public get activeFilters(): any {
    const filtered = {}
    for (const key in this.filter) {
      if (this.filter[key] !== blankFilter[key]) {
        filtered[key] = this.filter[key]
      }
    }
    return filtered
  }

  public async mounted() {
    this.hideBreadcrumbDisplay(true)

    this.getCustomers({
      page: this.searchFilters.page,
      sortBy: this.searchFilters.sortBy,
      sortDesc: this.searchFilters.sortDesc,
      customerNumber: this.searchFilters.customerNumber,
      customerName: this.searchFilters.customerName,
      filterByHasUsers: this.searchFilters.filterByHasUsers,
      filterByHasPendingPermissions: this.searchFilters.filterByHasPendingPermissions,
      city: this.filter.City,
      state: this.filter.State,
      postalCode: this.filter.PostalCode,
    })

    this.filter = {
      CustomerNumber: this.searchFilters.customerNumber,
      CustomerName: this.searchFilters.customerName,
      Users: this.searchFilters.filterByHasUsers,
      PermissionRequests: this.searchFilters.filterByHasPendingPermissions,
      City: this.searchFilters.city,
      State: this.searchFilters.state,
      PostalCode: this.searchFilters.postalCode,
    }

    this.sortOptions = {
      by: this.searchFilters.sortBy,
      desc: this.searchFilters.sortDesc,
    }
  }

  public sort(field: string) {
    if (this.sortOptions.by === field) {
      this.sortOptions = {
        ...this.sortOptions,
        desc: !this.sortOptions.desc,
      }
    } else {
      this.sortOptions = {
        by: field,
        desc: true,
      }
    }
  }

  @Watch('sortOptions')
  @Watch('filter', { deep: true })
  public filterBySearch() {
    if (!this.customers.loading) {
      this.getCustomers({
        page: 1,
        sortBy: this.sortOptions.by,
        sortDesc: this.sortOptions.desc,
        customerNumber: this.filter.CustomerNumber,
        customerName: this.filter.CustomerName,
        filterByHasUsers: this.filter.Users,
        filterByHasPendingPermissions: this.filter.PermissionRequests,
        city: this.filter.City,
        state: this.filter.State,
        postalCode: this.filter.PostalCode,
      })
    }
  }

  public get isFiltered(): boolean {
    for (const key in this.filter) {
      if (
        Object.prototype.hasOwnProperty.call(this.filter, key) && this.filter[key] &&
        (this.filter[key] !== blankFilter[key])
      ) {
        return true // At least one field is not empty
      }
    }
    return false // All fields are empty
  }

  public get isPrefiltered(): boolean {
    for (const key in this.searchFilters) {
      if (key !== 'page') {
        if (
          Object.prototype.hasOwnProperty.call(this.searchFilters, key) && this.searchFilters[key] &&
          this.searchFilters[key] !== blankSearchFilters[key]
        ) {
          return true // At least one field is not empty
        }
      }
    }
    return false // All fields are empty
  }

  public clearFilters() {
    this.filter = _.assign({}, blankFilter)

    this.filterOn = _.assign({}, blankFilterOn)

    this.sortOptions = {
      by: 'CustomerNumber',
      desc: true,
    }
  }

  public selectAll = false

  public selectedRows: ICustomer[] = []
  public items: ICustomer[] = []

  public get selectedItems(): ICustomer[] {
    return this.customers.List.filter((item) => this.selectedRows.includes(item))
  }

  public pageChanged(page: number) {
    this.getCustomers({
      page,
      sortBy: this.sortOptions.by,
      sortDesc: this.sortOptions.desc,
      customerNumber: this.filter.CustomerNumber,
      customerName: this.filter.CustomerName,
      filterByHasUsers: this.filter.Users,
      filterByHasPendingPermissions: this.filter.PermissionRequests,
      city: this.filter.City,
      state: this.filter.State,
      postalCode: this.filter.PostalCode,
    })
  }

  public onReject(item: any) {
    this.selectedItemToReject = item
    this.shouldShowRejectionModal = true
  }

  public rejectPermission() {
    const obj = { isApproved: false, rejectionStatus: this.rejectionReason, PermissionIds: [this.selectedItemToReject?.Id] }
    this.api.fetchV2({
      url: '/customer/permission-requests/batchRespond',
      obj,
      method: HttpMethod.Post,
    }).then(() => {
      this.toastSuccess('Updated successfully')
      this.filterBySearch()
    })
  }

  public onApprove(item: any) {
    this.selectedItemToApprove = item
    this.showApproveModal = true
  }

  public approvePermission() {
    this.api.fetchV2({
      url: '/customer/permission-requests/batchRespond',
      obj: { isApproved: true, PermissionIds: [this.selectedItemToApprove?.Id] },
      method: HttpMethod.Post,
    }).then(() => {
      this.toastSuccess('Updated successfully')
      this.filterBySearch()
    })
  }

  public isValidRejectionReason() {
    return this.rejectionReason && this.rejectionReason !== ''
  }

  public generatePlaceHolder(field: any): string {
    const fieldLabel = _.has(field, 'placeholder') ? field.placeholder : (field as any).label
    return 'Enter ' + fieldLabel
  }

  public fields = [
    {
      key: 'details',
      label: 'Toggle Details',
      thClass: 'responsive-col-hide text-left',
      tdClass: 'responsive-col-hide text-left',
      thStyle: { width: '10%' },
    },
    {
      key: 'CustomerNumber',
      label: 'Customer Number',
      thClass: 'text-left',
      tdClass: 'responsive-col-size text-left',
      thStyle: 'resp-th-30',
      isSortable: true,
      isFilterable: true,
      hideInlineFilter: true,
    },
    {
      key: 'CustomerName',
      label: 'Customer Name',
      thClass: 'text-left',
      tdClass: 'responsive-col-size text-left',
      thStyle: 'resp-th-30',
      isSortable: true,
      isFilterable: true,
      hideInlineFilter: true,
    },
    {
      key: 'City',
      label: 'City',
      thClass: 'text-left',
      tdClass: 'responsive-col-size text-left',
      thStyle: 'resp-th-30',
      isSortable: true,
      isFilterable: true,
      hideInlineFilter: true,
    },
    {
      key: 'State',
      label: 'State',
      thClass: 'text-left',
      tdClass: 'responsive-col-size text-left',
      thStyle: 'resp-th-30',
      isSortable: true,
      isFilterable: true,
      hideInlineFilter: true,
    },
    {
      key: 'PostalCode',
      label: 'Zipcode',
      thClass: 'text-left',
      tdClass: 'responsive-col-size text-left',
      thStyle: 'resp-th-30',
      isSortable: true,
      isFilterable: true,
      hideInlineFilter: true,
    },
  ]
}
</script>
<style>
/* Add custom styles for b-tab */
.nav-link.active {
  position: relative;
}

.nav-link.active::after {
  content: '';
  position: absolute;
  bottom: -1px;
  left: 0;
  width: 100%;
  height: 2px;
  background-color: #007bff;
}

.header-icon {
  font-size: 18px;
  /* Adjust the size as needed */
  color: #0b67b2 !important;
  cursor: pointer;
}

.clear-btn:hover {
  font-size: 18px;
  /* Adjust the size as needed */
  background-color: lightcoral !important;
  cursor: pointer;
  color: white;
}

.date-picker-btn {
  background: transparent;
}

.filter-tabs {
  flex-wrap: nowrap !important;
  white-space: nowrap;
  overflow-x: auto;
  overflow-y: hidden;
  padding-bottom: 1.5px;
  flex-direction: row !important;
}

.reset-filter-icon {
  display: none !important;
}

@media (max-width: 767px) {
  .actions-bar {
    padding: 5px !important;
  }

  .reset-filter-icon {
    display: block !important;
  }
}

.row-details-label {
  color: #000;
  font-weight: 500;
  text-align: right;
}

.th-custom-class {
  position: sticky;
  top: 0px;
  z-index: 2;
}

.filter-pills {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: medium;
  flex-wrap: wrap;
}
</style>
